import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import {
  isReportWidget,
  TRefBlockWidget,
  TReport,
  TReportEdition
} from 'types/GlobalReport'
import { toArray } from 'redux/utils'
import { sortAlphabeticalAsc, sortDateDesc } from 'utils/sortHelper'
import { TReportWidget } from 'types/GlobalWidget'

type StatusById = ApplicationState['ResourceStatusesStore']['byId']

function resolveReport(
  report: TReport,
  statusById: StatusById
): TReport {
  const status = statusById[report.id]

  return {
    ...report,
    favorite: status?.favorite ?? false,
    visited_at: status?.latest_visit ? new Date(status.latest_visit) : null
  }
}

export const selectReport = createSelector(
  [
    (state: ApplicationState) => state.ReportStore.reports,
    (state: ApplicationState) => state.ResourceStatusesStore.byId,
    (_: ApplicationState, reportId: string | null) => reportId
  ],
  (reports, statusById, reportId): TReport | null => {
    if (!reportId || !(reportId in reports)) return null

    const report = reports[reportId]

    return {
      ...resolveReport(report, statusById),
      blocks: [...report.blocks].sort((a, b) => a.index - b.index),
      report_filters: [...report.report_filters].sort(
        (a, b) => a.index - b.index
      )
    }
  }
)

export const selectFavoriteReports = createSelector(
  [
    (state: ApplicationState) => state.ReportStore.reports,
    (state: ApplicationState) => state.ResourceStatusesStore.byId
  ],
  (reports, statusById): TReport[] => {
    return toArray(reports)
      .filter((report) => statusById[report.id]?.favorite)
      .map((report) => resolveReport(report, statusById))
      .sort((a, b) => sortAlphabeticalAsc(a.title, b.title))
  }
)

export const selectReports = createSelector(
  [
    (state: ApplicationState) => state.ReportStore.reports,
    (state: ApplicationState) => state.ResourceStatusesStore.byId
  ],
  (reports, statusById): TReport[] => {
    return toArray(reports).map((report) => resolveReport(report, statusById))
  }
)

export const selectReportEditions = createSelector(
  [
    (state: ApplicationState) => state.ReportStore.reports,
    (state: ApplicationState) => state.ReportStore.editions,
    (_: ApplicationState, reportId: string | null) => reportId
  ],
  (reports, editions, reportId): TReportEdition[] => {
    if (!reportId || !(reportId in reports)) return []

    const editionIds = reports[reportId].editions

    return editionIds
      .map((eid) => editions[eid])
      .sort((a, b) => sortAlphabeticalAsc(a.created_at, b.created_at))
      .filter(Boolean)
  }
)

export const selectReportEdition = createSelector(
  [
    (state: ApplicationState) => state.ReportStore.editions,
    (_: ApplicationState, editionId: string | null) => editionId
  ],
  (editions, editionId): TReportEdition | null => {
    if (!editionId) return null

    const edition = editions[editionId]

    if (!edition) return null

    return {
      ...edition,
      blocks: [...edition.blocks].sort((a, b) => a.index - b.index)
    }
  }
)

export const selectRecentReports = createSelector(
  [
    (state: ApplicationState) => state.ReportStore.reports,
    (state: ApplicationState) => state.ResourceStatusesStore.byId
  ],
  (reports, statusById): TReport[] => {
    return toArray(reports)
      .filter((report) => statusById[report.id]?.latest_visit)
      .sort((a, b) => {
        const visitA = statusById[a.id]?.latest_visit
        const visitB = statusById[b.id]?.latest_visit
        const dateA = visitA ? new Date(visitA) : null
        const dateB = visitB ? new Date(visitB) : null

        return sortDateDesc(dateA, dateB)
      })
      .slice(0, 5)
      .map((report) => resolveReport(report, statusById))
  }
)

export const selectReportWidget = createSelector(
  [
    (state: ApplicationState) => state.ReportStore.widgets,
    (state: ApplicationState) => state.WidgetStore.widgets,
    (_: ApplicationState, id: string) => id
  ],
  (
    refWidgets,
    widgets,
    widgetId
  ): TReportWidget | (TRefBlockWidget & { dataVersion: number }) | null => {
    const widget = widgets[widgetId]

    if (widget && isReportWidget(widget)) {
      return widget
    }

    const refWidget = refWidgets[widgetId]

    if (!refWidget) {
      return null
    }

    return {
      ...refWidget,
      dataVersion: 0
    }
  }
)
